import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, UserX, Users as UsersIcon } from 'lucide-react';
import { ROLE_LABELS, STAFF_ROLES, type StaffRole, type StaffUser } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useUsers } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Toggle,
} from '../components/ui';

interface Draft {
  id?: string;
  email: string;
  fullName: string;
  role: StaffRole;
  password: string;
  pin: string;
  isActive: boolean;
}

const EMPTY: Draft = {
  email: '',
  fullName: '',
  role: 'WAITER',
  password: '',
  pin: '',
  isActive: true,
};

export function UsersAdminPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const { data: users, isLoading, error, refetch } = useUsers();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [deactivating, setDeactivating] = useState<StaffUser | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['users'] });
  const failed = (caught: unknown, fallback: string) =>
    toast.error(caught instanceof ApiError ? caught.message : fallback);

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const base = {
        email: value.email.trim().toLowerCase(),
        fullName: value.fullName.trim(),
        role: value.role,
        pin: value.pin.trim() || null,
        isActive: value.isActive,
      };
      if (value.id) {
        return api.users.update(value.id, {
          ...base,
          ...(value.password ? { password: value.password } : {}),
        });
      }
      return api.users.create({ ...base, password: value.password });
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
      toast.success('Staff account saved.');
    },
    onError: (caught) => failed(caught, 'Could not save that account.'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.users.deactivate(id),
    onSuccess: () => {
      invalidate();
      setDeactivating(null);
      toast.success('Account deactivated. Its history is kept.');
    },
    onError: (caught) => failed(caught, 'Could not deactivate that account.'),
  });

  const passwordRequired = draft !== null && !draft.id;
  const passwordValid = !passwordRequired ? draft?.password === '' || (draft?.password.length ?? 0) >= 8 : draft.password.length >= 8;

  return (
    <div className="mx-auto max-w-4xl px-5 py-6">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Staff</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Roles decide which screens each member of staff can reach.
          </p>
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setDraft({ ...EMPTY })}>
          New account
        </Button>
      </header>

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {users && users.length === 0 ? (
        <EmptyState icon={<UsersIcon size={30} />} title="No staff accounts" />
      ) : null}

      {users && users.length > 0 ? (
        <Card padded={false} className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="bg-raised/60 text-left text-[11px] uppercase tracking-wider text-ink-faint">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Since</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((account) => (
                <tr key={account.id} className="border-t border-line hover:bg-hover">
                  <td className="px-4 py-3 font-medium text-ink">
                    {account.fullName}
                    {account.id === me?.id ? (
                      <span className="ml-2 text-xs text-accent">you</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{account.email}</td>
                  <td className="px-4 py-3">
                    <Badge tone={account.role === 'ADMIN' ? 'accent' : 'neutral'}>
                      {ROLE_LABELS[account.role]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-faint">{formatDate(account.createdAt)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={account.isActive ? 'positive' : 'danger'}>
                      {account.isActive ? 'Active' : 'Disabled'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        aria-label="Edit"
                        onClick={() =>
                          setDraft({
                            id: account.id,
                            email: account.email,
                            fullName: account.fullName,
                            role: account.role,
                            password: '',
                            pin: account.pin ?? '',
                            isActive: account.isActive,
                          })
                        }
                        className="rounded p-1.5 text-ink-faint transition hover:text-accent"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        aria-label="Deactivate"
                        disabled={account.id === me?.id || !account.isActive}
                        onClick={() => setDeactivating(account)}
                        className="rounded p-1.5 text-ink-faint transition hover:text-danger disabled:opacity-30"
                      >
                        <UserX size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit staff account' : 'New staff account'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!draft?.email.trim() || !draft?.fullName.trim() || !passwordValid}
              onClick={() => draft && save.mutate(draft)}
            >
              Save
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <Field label="Full name">
              <Input
                autoFocus
                value={draft.fullName}
                onChange={(event) => setDraft({ ...draft, fullName: event.target.value })}
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={draft.email}
                onChange={(event) => setDraft({ ...draft, email: event.target.value })}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Role">
                <Select
                  value={draft.role}
                  onChange={(event) => setDraft({ ...draft, role: event.target.value as StaffRole })}
                >
                  {STAFF_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Station PIN (optional)" hint="4–6 digits.">
                <Input
                  inputMode="numeric"
                  value={draft.pin}
                  onChange={(event) => setDraft({ ...draft, pin: event.target.value })}
                />
              </Field>
            </div>
            <Field
              label={draft.id ? 'New password (leave blank to keep)' : 'Password'}
              hint="At least 8 characters."
              error={!passwordValid ? 'Password must be at least 8 characters' : undefined}
            >
              <Input
                type="password"
                autoComplete="new-password"
                value={draft.password}
                onChange={(event) => setDraft({ ...draft, password: event.target.value })}
              />
            </Field>
            <Toggle
              checked={draft.isActive}
              onChange={(isActive) => setDraft({ ...draft, isActive })}
              label="Can sign in"
            />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deactivating !== null}
        title={`Deactivate ${deactivating?.fullName ?? ''}?`}
        message="They will not be able to sign in. The account and its audit trail are kept."
        confirmLabel="Deactivate"
        loading={deactivate.isPending}
        onCancel={() => setDeactivating(null)}
        onConfirm={() => deactivating && deactivate.mutate(deactivating.id)}
      />
    </div>
  );
}
